import { __rest, __assign, __read, __spreadArray } from 'tslib';
import * as React from 'react';
import React__default, { useState, useMemo, createRef, useCallback, useEffect } from 'react';
import { Divider } from '@sravni/react-design-system/lib/Divider';
import { Layout } from '@sravni/react-design-system/lib/Layout';
import { Space } from '@sravni/react-design-system/lib/Space';
import { useAnalytics, useStyles, useTheme, useDebounceValue, mergeRefs, useAuthLogoutLink, useAuthLoginLink, useBrowserThemeDetector, preventBodyScroll, useIsMobile } from '@sravni/react-utils';
import { subscribeOnLoginSuccess } from '@sravni/utils/lib/auth';
import { Popover } from '@sravni/react-design-system/lib/Popover';
import { Link, Text } from '@sravni/react-design-system/lib/Typography';
import { Icon } from '@sravni/react-design-system/lib/Icon';
import { Row, Col } from '@sravni/react-design-system/lib/Grid';
import { Avatar } from '@sravni/react-design-system/lib/Avatar';
import { RegionDialog } from '@sravni/react-region-dialog';
import { PhoneDownload, Logout, Location, Theme, Sun, Cross, Menu } from '@sravni/react-icons';
import { Typography, Card, Space as Space$1, Icon as Icon$1, Badge } from '@sravni/react-design-system';
import DownloadQr from './assets/DownloadQr~eIYTMhaM.png';
import { Button } from '@sravni/react-design-system/lib/Button';
import { Logo as Logo$2 } from '@sravni/react-design-system/lib/Logo';
import { ThemeName } from '@sravni/design-system-theme';
import { Accordion } from '@sravni/react-design-system/lib/Accordion';

var decoratePropsByDefaultValues = function (props) {
    var _a = props.mode, mode = _a === void 0 ? "default" /* HeaderMode.DEFAULT */ : _a, _b = props.enableTheme, enableTheme = _b === void 0 ? false : _b, _c = props.fixedBackground, fixedBackground = _c === void 0 ? false : _c, _d = props.showContentWidget, showContentWidget = _d === void 0 ? true : _d, rest = __rest(props, ["mode", "enableTheme", "fixedBackground", "showContentWidget"]);
    return __assign({ enableTheme: enableTheme, fixedBackground: fixedBackground, showContentWidget: showContentWidget, mode: mode }, rest);
};
var splitProps = function (props) {
    var serviceURL = props.serviceURL, regions = props.regions, getServiceUrl = props.getServiceUrl, formatPath = props.formatPath, currentRegion = props.currentRegion, mode = props.mode, domains = props.domains, user = props.user, enableTheme = props.enableTheme, fixedBackground = props.fixedBackground, oidcHandlerPrefix = props.oidcHandlerPrefix, settings = props.settings, promo = props.promo, onOpen = props.onOpen, onClose = props.onClose, onMenuOpen = props.onMenuOpen, onMenuClose = props.onMenuClose, rest = __rest(props, ["serviceURL", "regions", "getServiceUrl", "formatPath", "currentRegion", "mode", "domains", "user", "enableTheme", "fixedBackground", "oidcHandlerPrefix", "settings", "promo", "onOpen", "onClose", "onMenuOpen", "onMenuClose"]);
    return [
        {
            serviceURL: serviceURL,
            regions: regions,
            getServiceUrl: getServiceUrl,
            formatPath: formatPath,
            currentRegion: currentRegion,
            mode: mode,
            domains: domains,
            user: user,
            enableTheme: enableTheme,
            fixedBackground: fixedBackground,
            oidcHandlerPrefix: oidcHandlerPrefix,
            settings: settings,
            promo: promo,
            onOpen: onOpen,
            onClose: onClose,
            onMenuOpen: onMenuOpen,
            onMenuClose: onMenuClose
        },
        rest,
    ];
};

var CustomPropsContext = React__default.createContext({});
var CustomPropsProvider = CustomPropsContext.Provider;

function useCustomProps() {
    return React.useContext(CustomPropsContext);
}

var MenuItemVisibleContext = React__default.createContext({
    item: undefined,
    setItem: function () { }
});
function MenuItemVisibleProvider(props) {
    var value = props.value, children = props.children;
    var _a = __read(useState(value), 2), item = _a[0], setItem = _a[1];
    var providerValue = useMemo(function () { return ({ item: item, setItem: setItem }); }, [item, setItem]);
    return React__default.createElement(MenuItemVisibleContext.Provider, { value: providerValue }, children);
}

function useMenuItemVisible() {
    var context = React.useContext(MenuItemVisibleContext);
    if (!context || Object.keys(context).length === 0)
        throw new Error('useMenuItemVisible hook must be inside MenuItemVisibleProvider');
    var item = context.item, setItem = context.setItem;
    return [item, setItem];
}

/** Delay the appearance of the menu in milliseconds, helps not to quickly switch to another menu
 * item when the cursor moves diagonally to it subitem (only for DesktopHeader)
 */
var MENU_DELAY = 250;

var convertModeToModeFlags = function (mode) {
    switch (mode) {
        case "landing" /* HeaderMode.LANDING */:
            return {
                isShowLogo: true,
                isShowMenu: false,
                isShowSearch: false,
                isShowUser: false
            };
        case "menuOff" /* HeaderMode.MENU_OFF */:
            return {
                isShowLogo: true,
                isShowMenu: false,
                isShowSearch: false,
                isShowUser: true
            };
        default:
            return {
                isShowLogo: true,
                isShowMenu: true,
                isShowSearch: false,
                isShowUser: true
            };
    }
};

/**
 * React hook is a wrapper under useAnalytics hook
 * @returns pushAnalyticEvent callback to send GA events
 */
var useAnalyticEvents = function () {
    var pushEvent = useAnalytics().pushEvent;
    var pushAnalyticEvent = React__default.useCallback(function (event, options) {
        var eventAction = (options === null || options === void 0 ? void 0 : options.eventAction) || '';
        var eventLabel = (options === null || options === void 0 ? void 0 : options.eventLabel) || '';
        var eventCategory = (options === null || options === void 0 ? void 0 : options.eventCategory) || '';
        var defaultEventProperties = {
            event: 'mainEvent',
            eventCategory: 'Меню'
        };
        // eslint-disable-next-line default-case
        switch (event) {
            case "login" /* EVENTS.LOGIN */:
                eventAction = 'Профиль';
                eventLabel = 'Войти';
                break;
            case "logout" /* EVENTS.LOGOUT */:
                eventAction = 'Профиль';
                eventLabel = 'Выйти';
                break;
            case "menuLinkClick" /* EVENTS.MENU_LINK_CLICK */:
                eventAction = 'Переход из меню';
                break;
            case "promoBannerClick" /* EVENTS.PROMO_BANNER_CLICK */:
                eventAction = 'Переход из меню|Кнопка партнера';
                break;
            case "promoLinkInView" /* EVENTS.PROMO_LINK_IN_VIEW */:
                eventAction = 'Показ кнопки партнера';
                break;
            case "topMenuShow" /* EVENTS.TOP_MENU_SHOW */:
            case "userMenuShow" /* EVENTS.USER_MENU_SHOW */:
                eventAction = 'Показ раздела';
                break;
            case "mobileBurgerClick" /* EVENTS.MOBILE_BURGER_CLICK */:
                eventAction = 'Бургер';
                break;
            case "regionDialogClick" /* EVENTS.REGION_DIALOG_CLICK */:
                eventAction = 'Выбор города';
                break;
            case "themeSwitcherClick" /* EVENTS.THEME_SWITCHER_CLICK */:
                eventAction = 'Активация|Переключение';
                eventCategory = 'Тема';
                break;
            case "themeDetectorChange" /* EVENTS.THEME_DETECTOR_CHANGE */:
                eventAction = 'Активация|Автоопределение';
                eventCategory = 'Тема';
                break;
            case "downloadApp" /* EVENTS.DOWNLOAD_APP */: {
                eventCategory = 'Меню профиля';
                break;
            }
        }
        pushEvent(__assign(__assign(__assign({}, defaultEventProperties), { eventAction: eventAction, eventLabel: eventLabel }), (eventCategory.length > 0 ? { eventCategory: eventCategory } : {})));
    }, [pushEvent]);
    return { pushAnalyticEvent: pushAnalyticEvent };
};

var promoParam = 'promo=1';
var contentWidgetParam = 'showContentWidget=1';

const observerMap = new Map();
const RootIds = new WeakMap();
let rootId = 0;
let unsupportedValue = undefined;
/**
 * Generate a unique ID for the root element
 * @param root
 */

function getRootId(root) {
  if (!root) return '0';
  if (RootIds.has(root)) return RootIds.get(root);
  rootId += 1;
  RootIds.set(root, rootId.toString());
  return RootIds.get(root);
}
/**
 * Convert the options to a string Id, based on the values.
 * Ensures we can reuse the same observer when observing elements with the same options.
 * @param options
 */


function optionsToId(options) {
  return Object.keys(options).sort().filter(key => options[key] !== undefined).map(key => {
    return `${key}_${key === 'root' ? getRootId(options.root) : options[key]}`;
  }).toString();
}

function createObserver(options) {
  // Create a unique ID for this observer instance, based on the root, root margin and threshold.
  let id = optionsToId(options);
  let instance = observerMap.get(id);

  if (!instance) {
    // Create a map of elements this observer is going to observe. Each element has a list of callbacks that should be triggered, once it comes into view.
    const elements = new Map();
    let thresholds;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        var _elements$get;

        // While it would be nice if you could just look at isIntersecting to determine if the component is inside the viewport, browsers can't agree on how to use it.
        // -Firefox ignores `threshold` when considering `isIntersecting`, so it will never be false again if `threshold` is > 0
        const inView = entry.isIntersecting && thresholds.some(threshold => entry.intersectionRatio >= threshold); // @ts-ignore support IntersectionObserver v2

        if (options.trackVisibility && typeof entry.isVisible === 'undefined') {
          // The browser doesn't support Intersection Observer v2, falling back to v1 behavior.
          // @ts-ignore
          entry.isVisible = inView;
        }

        (_elements$get = elements.get(entry.target)) == null ? void 0 : _elements$get.forEach(callback => {
          callback(inView, entry);
        });
      });
    }, options); // Ensure we have a valid thresholds array. If not, use the threshold from the options

    thresholds = observer.thresholds || (Array.isArray(options.threshold) ? options.threshold : [options.threshold || 0]);
    instance = {
      id,
      observer,
      elements
    };
    observerMap.set(id, instance);
  }

  return instance;
}
/**
 * @param element - DOM Element to observe
 * @param callback - Callback function to trigger when intersection status changes
 * @param options - Intersection Observer options
 * @param fallbackInView - Fallback inView value.
 * @return Function - Cleanup function that should be triggered to unregister the observer
 */


function observe(element, callback, options = {}, fallbackInView = unsupportedValue) {
  if (typeof window.IntersectionObserver === 'undefined' && fallbackInView !== undefined) {
    const bounds = element.getBoundingClientRect();
    callback(fallbackInView, {
      isIntersecting: fallbackInView,
      target: element,
      intersectionRatio: typeof options.threshold === 'number' ? options.threshold : 0,
      time: 0,
      boundingClientRect: bounds,
      intersectionRect: bounds,
      rootBounds: bounds
    });
    return () => {// Nothing to cleanup
    };
  } // An observer with the same options can be reused, so lets use this fact


  const {
    id,
    observer,
    elements
  } = createObserver(options); // Register the callback listener for this element

  let callbacks = elements.get(element) || [];

  if (!elements.has(element)) {
    elements.set(element, callbacks);
  }

  callbacks.push(callback);
  observer.observe(element);
  return function unobserve() {
    // Remove the callback from the callback list
    callbacks.splice(callbacks.indexOf(callback), 1);

    if (callbacks.length === 0) {
      // No more callback exists for element, so destroy it
      elements.delete(element);
      observer.unobserve(element);
    }

    if (elements.size === 0) {
      // No more elements are being observer by this instance, so destroy it
      observer.disconnect();
      observerMap.delete(id);
    }
  };
}

var getRefKey = function (item) { return "".concat(item.text).concat(item.href); };
var useCreateIntersectionObserverRefs = function (props) {
    var items = props.items, onChange = props.onChange, optionsOutput = props.options;
    var refs = useMemo(function () {
        return items.reduce(function (acc, item) {
            var _a;
            var ref = createRef();
            return __assign(__assign({}, acc), (_a = {}, _a[getRefKey(item)] = ref, _a));
        }, {});
    }, [items]);
    var options = useMemo(function () { return (__assign({ threshold: 1 }, optionsOutput)); }, [optionsOutput]);
    var handleIntersectionChange = useCallback(function (index) { return function (inView, entry) {
        onChange({
            inView: inView,
            item: items[index],
            index: index,
            entry: entry
        });
    }; }, [items, onChange]);
    useEffect(function () {
        var destroys = [];
        Object.values(refs).forEach(function (ref, index) {
            if (!ref.current)
                return;
            var destroy = observe(ref.current, handleIntersectionChange(index), options, true);
            destroys.push(destroy);
        });
        return function () {
            destroys.forEach(function (destroy) { return destroy(); });
        };
    }, [refs, items, handleIntersectionChange, options]);
    return {
        refs: refs,
        getRefKey: getRefKey
    };
};

var defaultDesktopMenu = [
    {
        text: 'Кредиты',
        href: '/kredity/onlajn-zayavka-na-kredit/',
        customStyle: '',
        isLink: true,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Подбор кредита',
                        href: '/kredity/onlajn-zayavka-na-kredit/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Wallet.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Кредитный рейтинг',
                        href: '/kredity/scoring/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Scoring.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Потребительские кредиты',
                        href: '/kredity/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Orders.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Рефинансирование',
                        href: '/kredity/promo/refinansirovanie/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Exchange.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Автокредиты',
                        href: '/avtokredity/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Car.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Кредиты для бизнеса',
                        href: '/biznes-kredity/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Briefcase.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Банкротство',
                        href: '/bankrotstvo/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/ChartDown.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Курсы обмена валют',
                        href: '/valjuty/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Рейтинг банков',
                        href: '/banki/rating/klientskij/?productCategory=natural',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Список банков',
                        href: '/banki/ ',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о банках',
                        href: '/banki/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Страхование',
        href: '',
        customStyle: '',
        isLink: false,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'ОСАГО',
                        href: '/osago/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Car.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Каско',
                        href: '/kasko/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Car.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование ипотеки',
                        href: '/strahovanie-ipoteki/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/HouseSafety.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование путешественников',
                        href: '/vzr/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Plane.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование квартиры',
                        href: '/strahovanie-nedvizhimosti/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/ShieldSafety.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Страхование спортсменов',
                        href: '/strahovanie-ns/sportivnaya/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/PrizeCup.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Добровольное медицинское страхование',
                        href: '/dms/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Stethoscope.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'От критических заболеваний',
                        href: '/strahovka-kriticheskie-bolezni/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Lungs.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование от укуса клеща',
                        href: '/strahovka-ot-klescha/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Bug.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование дома и дачи',
                        href: '/strahovanie-domov/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/InsuranceHouse.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 3',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Проверка КБМ',
                        href: '/osago/proverka-kbm/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Рейтинг страховых компаний',
                        href: '/strakhovye-kompanii/rating/klientskij/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Список страховых компаний',
                        href: '/strakhovye-kompanii/ ',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о страховых компаниях',
                        href: '/strakhovye-kompanii/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Статьи о страховании',
                        href: '/novosti/rubrika/strahovki/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Займы',
        href: '/zaimy/',
        customStyle: '',
        isLink: true,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Займы онлайн',
                        href: '/zaimy/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/PhoneDownload.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы на карту',
                        href: '/zaimy/na-kartu/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CreditCard.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы без отказа',
                        href: '/zaimy/na-kartu-bez-otkazov/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/ThumbUp.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы с плохой КИ',
                        href: '/zaimy/s-plokhoj-kreditnoj-istoriej/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/ChartDown.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы без процентов',
                        href: '/zaimy/bez-protsentov/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Percent.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Банкротство',
                        href: '/bankrotstvo/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/ChartDown.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Лучшие займы',
                        href: '/zaimy/top/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Fire.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы под залог ПТС',
                        href: '/zaimy/pod-pts/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Car.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы через Госуслуги',
                        href: '/zaimy/cherez-gosuslugi/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Fingerprint.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы до зарплаты',
                        href: '/zaimy/na-kartu-do-zarplaty/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/MyCredits.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Долгосрочные займы',
                        href: '/zaimy/dolgosrochnye/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CalendarSimple.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 3',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Рейтинг МФО',
                        href: '/zaimy/rating-mfo/?userRatingType=all',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Список МФО',
                        href: '/zaimy/mfo/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы об МФО',
                        href: '/zaimy/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Ипотека',
        href: '/ipoteka/',
        customStyle: '',
        isLink: true,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Ипотечные кредиты',
                        href: '/ipoteka/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/House.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Вторичное жилье',
                        href: '/ipoteka/zhile-na-vtorichnom-rynke/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/HouseVacations.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Новостройки',
                        href: '/ipoteka/zhile-na-pervichnom-rynke/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/House.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Строительство дома',
                        href: '/ipoteka/na-stroitelstvo-doma/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/HouseSafety.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Рефинансирование ипотеки',
                        href: '/ipoteka/refinansirovanie-ipoteki/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Exchange.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Ипотека в новостройках ПИК',
                        href: '/special/pik/?promo=1',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: '//f.sravni.ru/logotypes/menu/pikLogo.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование ипотеки',
                        href: '/strahovanie-ipoteki/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/HouseSafety.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 3',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Рейтинг банков',
                        href: '/banki/rating/klientskij/?productCategory=natural&reviewTag=mortgage&userRatingType=all',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Список банков',
                        href: '/banki/ ',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о банках',
                        href: '/banki/otzyvy/?tag=mortgage',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Карты',
        href: '/kredity/promo/karty/',
        customStyle: '',
        isLink: true,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Подбор кредитной карты',
                        href: '/kredity/promo/karty/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CreditCardGroupo.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Кредитные карты',
                        href: '/karty/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CardTimer.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Дебетовые карты',
                        href: '/debetovye-karty/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CardPlus.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Рейтинг банков',
                        href: '/banki/rating/klientskij/?productCategory=natural',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Список банков',
                        href: '/banki/ ',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о банках',
                        href: '/banki/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Вклады',
        href: '/vklady/',
        customStyle: '',
        isLink: true,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Вклады',
                        href: '/vklady/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Deposit.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Накопительные счета',
                        href: '/vklady/nakopitelnye/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Percent.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Валютные вклады',
                        href: '/vklady/valjutnie/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/DollarSign.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Вклады для пенсионеров',
                        href: '/vklady/pensionnye/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Users.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Вклады с пополнением',
                        href: '/vklady/s-popolneniem/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CoinsPlus.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Куда вложить деньги',
                        href: '/promo/bonus/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Star.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Брокерское обслуживание',
                        href: '/invest/brokerskoe-obsluzhivanie/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Briefcase.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Инвестиции в МФО',
                        href: '/invest/investicii-v-mfo/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CoinStack.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 3',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Рейтинг банков',
                        href: '/banki/rating/klientskij/?productCategory=natural',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Список банков',
                        href: '/banki/ ',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о банках',
                        href: '/banki/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Для бизнеса',
        href: '/biznes/',
        customStyle: 'white-space: nowrap;',
        isLink: true,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Сервис регистрации самозанятых',
                        href: '/biznes-oformlenie-samozanyatogo/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/IdentityCard.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Расчетно-кассовое обслуживание',
                        href: '/rko/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CardTerminal.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Кредиты',
                        href: '/biznes-kredity/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Briefcase.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Банковские гарантии',
                        href: '/biznes/bankovskie-garantii/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/MyCredits.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Эквайринг',
                        href: '/biznes/ekvajring/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Acquiring.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Вклады',
                        href: '/biznes-vklady/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CoinRub.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Регистрация бизнеса',
                        href: '/biznes-registration/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/BlankStamp.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Бухгалтерские услуги',
                        href: '/biznes-accounting/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/BusinessAccounting.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Бизнес на маркетплейс',
                        href: '/biznes-marketplace',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Management.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Лизинг',
                        href: '/biznes-leasing/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/ProductsSearch.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Внесение изменений в ИП и ООО',
                        href: '/biznes-vnesenie-izmeneniy/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CursorSelected.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Ликвидация ИП',
                        href: '/biznes-likvidaciya/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/FileCrossOut.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 3',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Рейтинг банков для бизнеса',
                        href: '/banki/rating/klientskij/?productCategory=legal',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Статьи о бизнесе',
                        href: '/novosti/teg/biznes/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о банках',
                        href: '/banki/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Образование',
        href: '/kursy/',
        customStyle: '',
        isLink: true,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Программирование',
                        href: '/kursy/programmirovanie/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Programming.svg',
                        isTextVisible: false,
                        children: []
                    },
                    {
                        text: 'Дизайн',
                        href: '/kursy/dizajn/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Design.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Управление',
                        href: '/kursy/upravlencheskie/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Briefcase.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Аналитика',
                        href: '/kursy/professii-analitik/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Analytics.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Маркетинг',
                        href: '/kursy/professii-marketolog/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Marketing.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Подготовка к ЕГЭ и ОГЭ',
                        href: '/ege-oge/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/BookOpen.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Разработка на Python',
                        href: '/kursy/programmirovanie-python/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Globe.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: '1С-программирование',
                        href: '/kursy/programmirovanie-1s/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Programming1C.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'QA-тестирование',
                        href: ' /kursy/programmirovanie-testirovshchik/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/QA.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Графический дизайн',
                        href: '/kursy/dizajn-graficheskij/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/GraphicDesign.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Веб-дизайн',
                        href: '/kursy/dizajn-web/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/WebDesign.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Английский язык',
                        href: '/kursy/anglijskij-yazyk/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Bubble.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 3',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Рейтинг курсов',
                        href: '/kursy/rating/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о курсах',
                        href: '/kursy/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Статьи о курсах',
                        href: '/novosti/teg/obrazovanie/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Список школ',
                        href: '/shkoly/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Журнал',
        href: "/novosti/?showContentWidget=1",
        customStyle: '',
        isLink: true,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Главное',
                        href: '/novosti/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Calculations.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Спецпроекты',
                        href: '/promo/special-projects/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Rocket.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Тесты и игры',
                        href: '/novosti/teg/testy-i-igry/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Gamepad.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Вопросы и ответы',
                        href: '/q/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/QnA.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Блоги компаний',
                        href: '/novosti/rubrika/novosti-partnerov/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Subscriptions.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Рейтинг экспертов',
                        href: '/u/rating/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/PrizeCup.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Карты',
                        href: '/novosti/rubrika/karty/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Вклады',
                        href: '/novosti/rubrika/vklady/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Кредиты',
                        href: '/novosti/rubrika/kredity/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Инвестиции',
                        href: '/novosti/rubrika/investicii/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Недвижимость',
                        href: '/novosti/rubrika/nedvizhimost/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование',
                        href: '/novosti/rubrika/strahovki/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Советы',
                        href: '/novosti/rubrika/sovety/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Карты',
                        href: '/novosti/rubrika/karty/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Вклады',
                        href: '/novosti/rubrika/vklady/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Кредиты',
                        href: '/novosti/rubrika/kredity/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Инвестиции',
                        href: '/novosti/rubrika/investicii/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Недвижимость',
                        href: '/novosti/rubrika/nedvizhimost/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование',
                        href: '/novosti/rubrika/strahovki/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Советы',
                        href: '/novosti/rubrika/sovety/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
];

var flatMenuLinks = function (menu, addCurrentLevelItem) {
    if (addCurrentLevelItem === void 0) { addCurrentLevelItem = true; }
    return menu.reduce(function (acc, submenu) {
        var children = submenu.children;
        var deepLevelLinks = flatMenuLinks(children, false);
        return acc.concat(__spreadArray(__spreadArray(__spreadArray([], __read((addCurrentLevelItem ? [submenu] : [])), false), __read(children), false), __read(deepLevelLinks), false));
    }, []);
};

function formatKey(key) {
    var _a = __read(key.split('-')), start = _a[0], rest = _a.slice(1);
    return "".concat(start).concat(!rest ? '' : rest.map(function (k) { return "".concat(k[0].toUpperCase()).concat(k.slice(1)); }).join(''));
}
var getCustomStyles = function (item) {
    var _a;
    var style = {};
    if ((_a = item.customStyle) === null || _a === void 0 ? void 0 : _a.length) {
        style = item.customStyle
            // .replace(/ /g, '')
            .split(';')
            .reduce(function (output, item) {
            var _a = __read(item.split(':'), 2), key = _a[0], value = _a[1];
            if (key && value) {
                output[formatKey(key)] = value;
            }
            return output;
        }, {});
    }
    return style;
};

/** Hardcode custom  styles for menu icons (these customization are not in the sitesettings yet) */
var customIcons = [
    {
        label: 'Ипотека в новостройках ПИК',
        href: "/special/pik/?".concat(promoParam),
        style: { padding: '6px' }
    },
    {
        label: 'Онлайн-курсы Skillbox',
        href: '/kursy/shkola/skillbox/',
        style: { background: '#3D3BFF', padding: '8px' }
    },
];
var customIconsMap = function () {
    return customIcons.reduce(function (acc, icon) {
        acc[icon.href] = icon.style;
        return acc;
    }, {});
};
var getCustomIconStyles = function (href) {
    var icons = customIconsMap();
    return icons[href] || {};
};

var generateUrl$1 = function (path, domain) {
    var hasDomain = typeof domain === 'string';
    var base = hasDomain ? domain : 'http://test.com';
    var url = new URL(path, base);
    if (!hasDomain)
        return "".concat(url.pathname).concat(url.search);
    return url.toString();
};
var decorateRelativeUrlByDomain = function (path, domain) {
    var isAbsoluteUrl = function (path) {
        try {
            // eslint-disable-next-line no-new
            new URL(path);
            return true;
        }
        catch (_) {
            return false;
        }
    };
    return isAbsoluteUrl(path) ? path : generateUrl$1(path, domain);
};
var makeRelAttribute = function (config) {
    if (!config)
        return '';
    var withNoReferrerParams = config.href.indexOf('promo=1') > -1 || config.href.indexOf('noreferrer=1') > -1;
    // @ts-ignore TODO исправить после обновления типизации
    var noReferrer = withNoReferrerParams || config.noreferrer ? 'noreferrer' : undefined;
    var noFollow = config.nofollow ? 'nofollow' : undefined;
    var rel = [noReferrer, noFollow].filter(function (tag) { return tag; }).join(' ');
    return rel || undefined;
};

var modules_3b87a8fb = {"container":"_iqahzu"};
/*SRAVNI__src/components/InjectedIcon/styles/InjectedIcon.module.less__SRAVNI*/;

var InjectedIcon$1 = function (_a) {
    var iconSrc = _a.iconSrc;
    return (React__default.createElement("span", { className: modules_3b87a8fb.container, dangerouslySetInnerHTML: { __html: iconSrc } }));
};

var modules_51430e0a = {"item":"_vw6vv9"};
/*SRAVNI__src/components/MenuItem/styles/MenuItem.module.less__SRAVNI*/;

var MenuItem$1 = React__default.forwardRef(function (props, ref) {
    var item = props.item, _a = props.size, size = _a === void 0 ? 14 : _a, restProps = __rest(props, ["item", "size"]);
    var text = item.text, href = item.href, openInNew = item.openInNew, iconSrc = item.iconSrc, icon = item.icon;
    var domains = useCustomProps().domains;
    var cx = useStyles(modules_51430e0a);
    return (React__default.createElement(Link, __assign({ ref: ref, "data-label": text, color: "black", href: (domains === null || domains === void 0 ? void 0 : domains.base) ? decorateRelativeUrlByDomain(href, domains.base) : href, style: getCustomStyles(item), target: openInNew ? '_blank' : undefined, rel: makeRelAttribute(item) }, restProps),
        React__default.createElement(Space, { align: "center", inline: true, size: 12, className: cx('item') },
            iconSrc ? (React__default.createElement(Icon, { size: 16, color: "gray", style: getCustomIconStyles(href) },
                React__default.createElement(InjectedIcon$1, { iconSrc: iconSrc }))) : null,
            icon && !iconSrc ? (React__default.createElement(Icon, { size: 16, color: "gray", style: getCustomIconStyles(href) },
                React__default.createElement("img", { src: icon, alt: "" }))) : null,
            React__default.createElement(Text, { as: "span", strong: true, size: size }, text))));
});
MenuItem$1.displayName = 'MenuItem';

var convertHiddenItemsObjectToSortedByKeyArray = function (hiddenItems) {
    return Object.keys(hiddenItems)
        .sort()
        .map(function (key) { return hiddenItems[key]; });
};
var useHiddenTopLinksObserver = function (props) {
    var _a = __read(useState({}), 2), hiddenItems = _a[0], setHiddenItems = _a[1];
    var _b = __read(useState({}), 2), visibleEntries = _b[0], setVisibleEntries = _b[1];
    var handleChange = useCallback(function (_a) {
        var inView = _a.inView, item = _a.item, index = _a.index, entry = _a.entry;
        if (inView) {
            setHiddenItems(function (hiddenItems) {
                var _a = hiddenItems, _b = "".concat(index); _a[_b]; var rest = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
                return rest;
            });
            setVisibleEntries(function (visibleEntries) {
                var _a;
                return (__assign(__assign({}, visibleEntries), (entry ? (_a = {}, _a[String(index)] = entry, _a) : {})));
            });
        }
        else {
            setHiddenItems(function (hiddenItems) {
                var _a;
                return (__assign(__assign({}, hiddenItems), (_a = {}, _a[String(index)] = item, _a)));
            });
            setVisibleEntries(function (visibleEntries) {
                var _a = visibleEntries, _b = "".concat(index); _a[_b]; var rest = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
                return rest;
            });
        }
    }, []);
    var lastRightVisibleEntry = useMemo(function () {
        return Object.keys(visibleEntries)
            .sort()
            .map(function (key) { return visibleEntries[key]; })
            .pop();
    }, [visibleEntries]);
    var _c = useCreateIntersectionObserverRefs(__assign(__assign({}, props), { onChange: handleChange })), refs = _c.refs, getRefKey = _c.getRefKey;
    return {
        refs: refs,
        getRefKey: getRefKey,
        lastRightVisibleEntry: lastRightVisibleEntry,
        hiddenItems: convertHiddenItemsObjectToSortedByKeyArray(hiddenItems)
    };
};

var modules_25250a06 = {"container":"_cqvwg9","item":"_1s458o7","containerWithMore":"_42cxgx","itemMore":"_dqlogv","itemMoreVisible":"_t38y5e","moreTopLinksDialog":"_1rgn3mf","moreTopLink":"_1evwpzq"};
/*SRAVNI__src/components/DesktopMenu/styles/DesktopMenu.module.less__SRAVNI*/;

var modules_3d0bf2ca = {"wrapper":"_ce0cfd"};
/*SRAVNI__src/components/ContentWidget/styles/ContentWidget.module.less__SRAVNI*/;

var generateUrl = function (theme, domain) {
    var origin = typeof window !== 'undefined' ? window.location.origin : undefined;
    try {
        var url = new URL("/ugc-widgets/news-widget/horizontal/header/".concat(theme, "/"), domain || origin);
        return url.toString();
    }
    catch (e) {
        console.error("".concat(domain, " is wrong url base"));
        return '';
    }
};
var ContentWidget$1 = React.forwardRef(function (props, ref) {
    var domain = props.domain, className = props.className, rest = __rest(props, ["domain", "className"]);
    var cx = useStyles(modules_3d0bf2ca);
    var _a = __read(useTheme(), 1), theme = _a[0];
    var url = generateUrl(theme, domain);
    return (React.createElement("div", __assign({ ref: ref, className: cx('wrapper', className) }, rest),
        React.createElement("iframe", { src: url, width: 573, height: 350 })));
});

var modules_8fa60410 = {"dropdown":"_10mtdc7","dropdownContent":"_au9rt5","dropdownVisible":"_1gfpjwe"};
/*SRAVNI__src/components/DesktopMenu/styles/DesktopMenuDropdown.module.less__SRAVNI*/;

var DesktopMenuDropdown$1 = React__default.forwardRef(function (props, ref) {
    var item = props.item, isContentWidgetShown = props.isContentWidgetShown, isVisible = props.isVisible, hasWidget = props.hasWidget, getRef = props.getRef, handleItemClick = props.onItemClick, restProps = __rest(props, ["item", "isContentWidgetShown", "isVisible", "hasWidget", "getRef", "onItemClick"]);
    var cx = useStyles(modules_8fa60410);
    var domains = useCustomProps().domains;
    return (React__default.createElement("div", __assign({ className: cx('dropdown', { dropdownVisible: isVisible }) }, restProps, { ref: ref }),
        React__default.createElement("div", { className: cx('dropdownContent') },
            React__default.createElement(Row, { gutter: 20 },
                item.children.slice(0, hasWidget ? 2 : undefined).map(function (group) { return (React__default.createElement(Col, { key: group.text, span: hasWidget ? 3 : 4 },
                    React__default.createElement(Space, { size: 20, direction: "vertical", align: "stretch" }, group.children.map(function (menuItem) { return (React__default.createElement(MenuItem$1, { ref: getRef === null || getRef === void 0 ? void 0 : getRef(menuItem), key: menuItem.href, item: menuItem, onClick: handleItemClick, size: 16 })); })))); }),
                // Загружаем виджет только когда он показывается на экране первый раз
                hasWidget && isContentWidgetShown ? (React__default.createElement(Col, { span: 6 },
                    React__default.createElement(ContentWidget$1, { domain: (domains === null || domains === void 0 ? void 0 : domains.base) || '' }))) : null))));
});
DesktopMenuDropdown$1.displayName = 'DesktopMenuDropdown';

var modules_a4a8ece5 = {"itemWrapper":"_uugh4n","item":"_qfvhlk"};
/*SRAVNI__src/components/DesktopMenu/styles/DesktopMenuTopLink.module.less__SRAVNI*/;

var DesktopMenuTopLink$1 = React__default.forwardRef(function (props, ref) {
    var className = props.className, item = props.item, children = props.children, restProps = __rest(props, ["className", "item", "children"]);
    var isLink = item.isLink, text = item.text, href = item.href, openInNew = item.openInNew;
    var domains = useCustomProps().domains;
    var cx = useStyles(modules_a4a8ece5);
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var handleLinkClick = React__default.useCallback(function (event) {
        var label = event.currentTarget.dataset.label || '';
        pushAnalyticEvent("menuLinkClick" /* EVENTS.MENU_LINK_CLICK */, { eventLabel: "".concat(label, "|\u041F\u0435\u0440\u0435\u0445\u043E\u0434 \u043F\u043E \u0440\u0430\u0437\u0434\u0435\u043B\u0443") });
    }, [pushAnalyticEvent]);
    return (React__default.createElement("div", __assign({ className: cx(className, 'itemWrapper') }, restProps, { ref: ref }),
        isLink ? (React__default.createElement(Link, { "data-label": text, color: "black", href: (domains === null || domains === void 0 ? void 0 : domains.base) ? decorateRelativeUrlByDomain(href, domains.base) : href, target: openInNew ? '_blank' : undefined, rel: makeRelAttribute(item), onClick: handleLinkClick, className: cx('item') },
            React__default.createElement(Text, { strong: true }, text))) : (React__default.createElement(Text, { "data-label": text, className: cx('item'), strong: true }, text)),
        children));
});
DesktopMenuTopLink$1.displayName = 'DesktopMenuTopLink';

var DesktopMenu$1 = function (props) {
    var className = props.className, restProps = __rest(props, ["className"]);
    var cx = useStyles(modules_25250a06);
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var _a = useCustomProps(), _b = _a.settings, settings = _b === void 0 ? {} : _b, onOpen = _a.onOpen, onMenuOpen = _a.onMenuOpen, onClose = _a.onClose, onMenuClose = _a.onMenuClose;
    var _c = settings.menu, menu = _c === void 0 ? defaultDesktopMenu : _c;
    var _d = __read(useMenuItemVisible(), 2), activeItem = _d[0], setActiveItem = _d[1];
    var activeItemTextDebounced = useDebounceValue(activeItem, MENU_DELAY);
    var _e = __read(useState(false), 2), isMoreTopLinksDialogVisible = _e[0], setIsMoreTopLinksDialogVisible = _e[1];
    var _f = __read(useState(false), 2), isContentWidgetShown = _f[0], setIsContentWidgetShown = _f[1];
    var promoLinks = useMemo(function () { return flatMenuLinks(menu).filter(function (item) { return item.href.includes(promoParam); }); }, [menu]);
    var handleTopLinkMouseEnter = useCallback(function (item, hasWidget) { return function () {
        setActiveItem(item);
        setIsMoreTopLinksDialogVisible(false);
        if (hasWidget)
            setIsContentWidgetShown(true);
        onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        onMenuOpen === null || onMenuOpen === void 0 ? void 0 : onMenuOpen(item);
    }; }, [onMenuOpen, onOpen, setActiveItem]);
    var handleTopLinkMouseLeave = useCallback(function () {
        setActiveItem(undefined);
        onClose === null || onClose === void 0 ? void 0 : onClose();
        onMenuClose === null || onMenuClose === void 0 ? void 0 : onMenuClose();
    }, [onClose, onMenuClose, setActiveItem]);
    var handleMoreTopLinksTriggerClick = useCallback(function () { return setIsMoreTopLinksDialogVisible(function (isVisible) { return !isVisible; }); }, []);
    var handleMoreTopLinksDialogCloseClick = useCallback(function () { return setIsMoreTopLinksDialogVisible(false); }, []);
    var handleMenuItemClick = useCallback(function (event) {
        var label = event.currentTarget.dataset.label || '';
        pushAnalyticEvent("menuLinkClick" /* EVENTS.MENU_LINK_CLICK */, { eventLabel: "".concat(activeItemTextDebounced, "|").concat(label) });
    }, [activeItemTextDebounced, pushAnalyticEvent]);
    var handlePromoLinkIntersectionChange = useCallback(function (data) {
        var inView = data.inView, item = data.item;
        var text = item.text, href = item.href;
        if (inView) {
            pushAnalyticEvent("promoLinkInView" /* EVENTS.PROMO_LINK_IN_VIEW */, { eventLabel: "".concat(activeItemTextDebounced, "|").concat(text, "|").concat(href) });
        }
    }, [activeItemTextDebounced, pushAnalyticEvent]);
    var _g = useHiddenTopLinksObserver({
        items: menu
    }), hiddenItemsIntersectionRefs = _g.refs, getHiddenItemsRefKey = _g.getRefKey, hiddenItems = _g.hiddenItems, lastRightVisibleEntry = _g.lastRightVisibleEntry;
    var _h = useCreateIntersectionObserverRefs({
        items: promoLinks,
        onChange: handlePromoLinkIntersectionChange
    }), promoIntersectionRefs = _h.refs, getPromoRefKey = _h.getRefKey;
    // Затыкаю дырку с типизацией. найти лучшее решение ошибки
    // Type 'RefObject<HTMLElement>' is not assignable to type 'Ref<HTMLAnchorElement>'.
    var getMenuItemRef = useCallback(function (item) { return promoIntersectionRefs[getPromoRefKey(item)]; }, [getPromoRefKey, promoIntersectionRefs]);
    var isMoreButtonVisible = hiddenItems.length > 0;
    var moreButtonLeftCoordinate = useMemo(function () {
        var target = typeof (lastRightVisibleEntry === null || lastRightVisibleEntry === void 0 ? void 0 : lastRightVisibleEntry.target) !== 'undefined' ? lastRightVisibleEntry.target : undefined;
        if (target) {
            return target.offsetLeft + target.offsetWidth;
        }
        // Делаем кнопку невидимой
        return 9999999;
    }, [lastRightVisibleEntry]);
    useEffect(function () {
        if (activeItemTextDebounced)
            pushAnalyticEvent("topMenuShow" /* EVENTS.TOP_MENU_SHOW */, { eventLabel: "".concat(activeItemTextDebounced, "|\u041D\u0430\u0432\u0435\u0434\u0435\u043D\u0438\u0435") });
    }, [activeItemTextDebounced, pushAnalyticEvent]);
    return (React__default.createElement("div", __assign({ className: cx('container', className, { containerWithMore: isMoreButtonVisible }) }, restProps),
        menu.map(function (topLink) {
            var hasWidget = topLink.href.includes(contentWidgetParam);
            return (React__default.createElement(DesktopMenuTopLink$1, { ref: mergeRefs(hiddenItemsIntersectionRefs[getHiddenItemsRefKey(topLink)], promoIntersectionRefs[getPromoRefKey(topLink)]), className: cx('item'), key: topLink.text, item: topLink, style: getCustomStyles(topLink), onMouseEnter: handleTopLinkMouseEnter(topLink.text, hasWidget), onMouseLeave: handleTopLinkMouseLeave },
                React__default.createElement(DesktopMenuDropdown$1, { key: topLink.href + topLink.text, item: topLink, getRef: getMenuItemRef, hasWidget: hasWidget, isVisible: topLink.text === activeItemTextDebounced, isContentWidgetShown: isContentWidgetShown, onItemClick: handleMenuItemClick })));
        }),
        React__default.createElement(Popover, { className: cx('moreTopLinksDialog'), visible: isMoreTopLinksDialogVisible, content: React__default.createElement(Space, { direction: "vertical" }, hiddenItems.map(function (item) { return (React__default.createElement(MenuItem$1, { key: item.text, size: 16, item: item, className: cx('moreTopLink') })); })), onClose: handleMoreTopLinksDialogCloseClick },
            React__default.createElement(Link, { strong: true, color: "black", style: { left: "".concat(moreButtonLeftCoordinate, "px") }, className: cx('itemMore', { itemMoreVisible: isMoreButtonVisible }), onClick: handleMoreTopLinksTriggerClick }, "\u0415\u0449\u0451"))));
};
DesktopMenu$1.displayName = 'DesktopMenu';

var formatPhone = function (phone) {
    if (!phone) {
        return '';
    }
    var tmp = phone.replace(/\D/g, '');
    return "+".concat(tmp.slice(0, 1), " ").concat(tmp.slice(1, 4), " ").concat(tmp.slice(4, 7), "-").concat(tmp.slice(7, 9), "-").concat(tmp.slice(9));
};
var getUserName = function (user) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    var nickname = user.nickname, _a = user.first_name, first_name = _a === void 0 ? '' : _a, phone_number = user.phone_number, email = user.email;
    var name = first_name.trim();
    if (name.length > 0)
        return name;
    if (nickname && nickname.trim().length > 0)
        return nickname;
    if (phone_number && phone_number.length > 0)
        return formatPhone(phone_number);
    if (email && email.length > 0)
        return email;
    return undefined;
};

var defaultUserMenu = [
    {
        text: 'Баланс',
        isTextVisible: true,
        href: 'https://sravni.id/user/profile/wallet/',
        customStyle: '',
        icon: 'https://s91588.cdn.ngenix.net/shared/icons/WalletBag.svg',
        noindex: false,
        nofollow: false,
        isLink: true,
        openInNew: false,
        children: []
    },
    {
        text: 'Кредитный рейтинг',
        isTextVisible: true,
        href: 'https://sravni.market/scoring/',
        customStyle: '',
        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Scoring.svg',
        noindex: false,
        nofollow: false,
        isLink: true,
        openInNew: false,
        children: []
    },
    {
        text: 'Кредиты',
        isTextVisible: true,
        href: 'https://sravni.id/user/profile/credits/',
        customStyle: '',
        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Orders.svg',
        noindex: false,
        nofollow: false,
        isLink: true,
        openInNew: false,
        children: []
    },
    {
        text: 'Вклады',
        isTextVisible: true,
        href: 'https://sravni.id/user/profile/savings/',
        customStyle: '',
        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Deposit.svg',
        noindex: false,
        nofollow: false,
        isLink: true,
        openInNew: false,
        children: []
    },
    {
        text: 'Страховые полисы',
        isTextVisible: true,
        href: 'https://sravni.id/user/profile/insurance-policies/',
        customStyle: '',
        icon: 'https://s91588.cdn.ngenix.net/shared/icons/ShieldSafety.svg',
        noindex: false,
        nofollow: false,
        isLink: true,
        openInNew: false,
        children: []
    },
    {
        text: 'Ответы и комментарии',
        isTextVisible: true,
        href: 'https://sravni.id/user/profile/activity/',
        customStyle: '',
        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Review.svg',
        noindex: false,
        nofollow: false,
        isLink: true,
        openInNew: false,
        children: []
    },
    {
        text: 'Приведи друга',
        isTextVisible: true,
        href: 'https://www.sravni.ru/promo/sharefriend/',
        customStyle: '',
        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Needlework.svg',
        noindex: false,
        nofollow: false,
        isLink: true,
        openInNew: false,
        children: []
    },
    {
        text: 'Профиль',
        isTextVisible: true,
        href: 'https://sravni.id/user/profile/',
        customStyle: '',
        icon: 'https://s91588.cdn.ngenix.net/shared/icons/User.svg',
        noindex: false,
        nofollow: false,
        isLink: true,
        openInNew: false,
        children: []
    },
];

var modules_2fceba91 = {"card":"_1yzsxsp","noWrap":"_2ek8n","minContent":"_933ht6","text":"_161m0sb","qrWrapper":"_1br7j2s","qr":"_12mnde2","dark":"_176qjtk"};
/*SRAVNI__src/components/DownloadApp/styles/DownloadApp.module.less__SRAVNI*/;

var DownloadApp$1 = function (_a) {
    var isMobile = _a.isMobile;
    var cx = useStyles(modules_2fceba91);
    var cardClassNames = cx('card', {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'h-mt-12': isMobile,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'h-mt-8': !isMobile
    });
    var titleClassNames = cx('minContent', 'dark', {
        noWrap: isMobile
    });
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var handleClick = useCallback(function () {
        pushAnalyticEvent("downloadApp" /* EVENTS.DOWNLOAD_APP */, {
            eventAction: 'Переход к установке приложения',
            eventLabel: 'Меню профиля|Общая кнопка'
        });
    }, [pushAnalyticEvent]);
    return (React__default.createElement(Typography.Link, { href: "https://www.sravni.ru/analytics-links/v1/redirects/mobileapps/?placement=sravni_lk", onClick: handleClick },
        React__default.createElement(Card, { color: "blue", size: 16, className: cardClassNames },
            React__default.createElement(Space$1, { align: "center", size: 12 },
                React__default.createElement(Icon$1, { icon: React__default.createElement(PhoneDownload, null), className: cx('dark') }),
                React__default.createElement(Space$1, { size: 4, direction: "vertical", justify: "center", className: cx('minContent') },
                    React__default.createElement(Space$1, { size: isMobile ? 8 : 4, align: isMobile ? 'center' : 'end' },
                        React__default.createElement(Typography.Heading, { level: 5, className: titleClassNames }, "\u0421\u043A\u0430\u0447\u0430\u0439\u0442\u0435 \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u0435"),
                        React__default.createElement(Badge, { text: "1", color: "red", variant: "primary", shape: "circle" })),
                    React__default.createElement(Typography.Text, { size: 12, className: cx('text') },
                        "\u0411\u044B\u0441\u0442\u0440\u0430\u044F \u0443\u0441\u0442\u0430\u043D\u043E\u0432\u043A\u0430 ",
                        isMobile && React__default.createElement(Typography.Link, null, "\u0421\u043A\u0430\u0447\u0430\u0442\u044C"))),
                !isMobile && (React__default.createElement(Space$1, { justify: "center", align: "center", className: cx('qrWrapper') },
                    React__default.createElement("img", { src: String(DownloadQr), className: cx('qr') })))))));
};

var LogoutButton = function (props) {
    var onClick = props.onClick, rest = __rest(props, ["onClick"]);
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var oidcHandlerPrefix = useCustomProps().oidcHandlerPrefix;
    var authLogoutLinkProps = useAuthLogoutLink({ prefix: oidcHandlerPrefix, isNewWindow: false });
    var handleClick = useCallback(function (event) {
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
        pushAnalyticEvent("logout" /* EVENTS.LOGOUT */);
    }, [onClick, pushAnalyticEvent]);
    return (React__default.createElement(Link, __assign({ color: "gray" }, authLogoutLinkProps, rest, { onClick: handleClick }),
        React__default.createElement(Space, { align: "center", size: 12 },
            React__default.createElement(Icon, { icon: React__default.createElement(Logout, null) }),
            React__default.createElement(Text, { size: 16, strong: true, className: "h-color-D60" }, "\u0412\u044B\u0439\u0442\u0438"))));
};
LogoutButton.displayName = 'LogoutButton';

var modules_eba0b2d9 = {"menu":"_12hrz5v","item":"_1qk6v52","divider":"_h0v2ij"};
/*SRAVNI__src/components/UserMenu/styles/UserMenu.module.less__SRAVNI*/;

var UserMenu$1 = function (props) {
    var className = props.className, onRegionSelectClick = props.onRegionSelectClick, restProps = __rest(props, ["className", "onRegionSelectClick"]);
    var _a = useCustomProps(), _b = _a.settings, settings = _b === void 0 ? {} : _b, currentRegion = _a.currentRegion;
    var cx = useStyles(modules_eba0b2d9);
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var _c = settings.userMenu, userMenu = _c === void 0 ? defaultUserMenu : _c;
    var topGroup = userMenu.slice(0, -1);
    var bottomGroup = (userMenu || []).slice(-1);
    var handleLinkClick = React__default.useCallback(function (event) {
        var label = event.currentTarget.dataset.label || '';
        pushAnalyticEvent("menuLinkClick" /* EVENTS.MENU_LINK_CLICK */, { eventLabel: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C|".concat(label) });
    }, [pushAnalyticEvent]);
    var handleRegionSelectClick = React__default.useCallback(function () {
        onRegionSelectClick === null || onRegionSelectClick === void 0 ? void 0 : onRegionSelectClick();
        pushAnalyticEvent("regionDialogClick" /* EVENTS.REGION_DIALOG_CLICK */, { eventLabel: "\u0412\u044B\u0437\u043E\u0432 \u043F\u043E\u043F\u0430\u043F\u0430" });
    }, [onRegionSelectClick, pushAnalyticEvent]);
    return (React__default.createElement("div", __assign({ className: cx('menu', className) }, restProps),
        topGroup.map(function (item) { return (React__default.createElement(Link, { "data-label": item.text, color: "black", key: item.href, href: item.href, onClick: handleLinkClick, className: cx('item') },
            React__default.createElement(Space, { align: "center", size: 12 },
                !!item.iconSrc && React__default.createElement(Icon, { size: 20, icon: React__default.createElement(InjectedIcon$1, { iconSrc: item.iconSrc }) }),
                item.icon && !item.iconSrc ? React__default.createElement(Icon, { size: 20, icon: React__default.createElement("img", { src: item.icon, alt: "" }) }) : null,
                React__default.createElement(Text, { size: 16, strong: true }, item.text)))); }),
        React__default.createElement(Divider, { className: cx('divider') }),
        React__default.createElement(DownloadApp$1, { isMobile: true }),
        React__default.createElement(Link, { color: "black", onClick: handleRegionSelectClick, className: cx('item') },
            React__default.createElement(Space, { align: "center", size: 12 },
                React__default.createElement(Icon, { icon: React__default.createElement(Location, null) }),
                React__default.createElement(Space, { direction: "vertical" },
                    React__default.createElement(Text, { size: 12, className: "h-color-D30" }, "\u041C\u043E\u0439 \u0433\u043E\u0440\u043E\u0434"),
                    React__default.createElement(Text, { size: 16, strong: true }, (currentRegion === null || currentRegion === void 0 ? void 0 : currentRegion.name) ? currentRegion.name : 'Выберите город')))),
        bottomGroup.map(function (item) { return (React__default.createElement(Link, { "data-label": item.text, color: "black", key: item.href, href: item.href, onClick: handleLinkClick, className: cx('item') },
            React__default.createElement(Space, { align: "center", size: 12 },
                !!item.iconSrc && React__default.createElement(Icon, { size: 20, icon: React__default.createElement(InjectedIcon$1, { iconSrc: item.iconSrc }) }),
                item.icon && !item.iconSrc ? React__default.createElement(Icon, { size: 20, icon: React__default.createElement("img", { src: item.icon }) }) : null,
                React__default.createElement(Text, { size: 16, strong: true }, item.text)))); }),
        React__default.createElement(Divider, { className: cx('divider') }),
        React__default.createElement(LogoutButton, { className: cx('item') })));
};
UserMenu$1.displayName = 'UserMenu';

var modules_6049fac5 = {"userInfo":"_19s4oui","dropdownWrapper":"_14vx3bj","dropdown":"_115346v","dropdownArrow":"_1qndqns","userName":"_1xlj0jo"};
/*SRAVNI__src/components/DesktopUserBlock/styles/DesktopUserBlock.module.less__SRAVNI*/;

var DesktopUserBlock$1 = function (props) {
    var className = props.className, onMouseEnter = props.onMouseEnter, restProps = __rest(props, ["className", "onMouseEnter"]);
    var _a = useCustomProps(), user = _a.user, _b = _a.serviceURL, serviceURL = _b === void 0 ? '/' : _b, formatPath = _a.formatPath, regions = _a.regions;
    var cx = useStyles(modules_6049fac5);
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var userName = user ? getUserName(user) : undefined;
    var _c = __read(useState(false), 2), isVisibleRegionDialog = _c[0], setIsVisibleRegionDialog = _c[1];
    var handleRegionDialogCloseClick = useCallback(function () { return setIsVisibleRegionDialog(false); }, [setIsVisibleRegionDialog]);
    var handleRegionSelectClick = useCallback(function () { return setIsVisibleRegionDialog(true); }, [setIsVisibleRegionDialog]);
    var handleRegionDialogChange = useCallback(function (region) {
        pushAnalyticEvent("regionDialogClick" /* EVENTS.REGION_DIALOG_CLICK */, { eventLabel: region.name });
    }, [pushAnalyticEvent]);
    var handleUserInfoMouseEnter = useCallback(function (event) {
        onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter(event);
        pushAnalyticEvent("userMenuShow" /* EVENTS.USER_MENU_SHOW */, { eventLabel: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C|\u041D\u0430\u0432\u0435\u0434\u0435\u043D\u0438\u0435" });
        pushAnalyticEvent("downloadApp" /* EVENTS.DOWNLOAD_APP */, {
            eventAction: 'Показ ссылки в Store',
            eventLabel: "\u041C\u0435\u043D\u044E \u043F\u0440\u043E\u0444\u0438\u043B\u044F|\u041E\u0431\u0449\u0430\u044F \u043A\u043D\u043E\u043F\u043A\u0430 \u0438 QR"
        });
    }, [pushAnalyticEvent, onMouseEnter]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Space, __assign({ align: "center", size: 12, className: cx('userInfo', className) }, restProps, { onMouseEnter: handleUserInfoMouseEnter }),
            (user === null || user === void 0 ? void 0 : user.sub) ? React__default.createElement(Avatar, { size: 36, src: "//f.sravni.ru/avatars/".concat(user.sub, ".svg") }) : null,
            React__default.createElement(Space, { direction: "vertical", align: "start" },
                userName ? (React__default.createElement(Text, { size: 12, className: "h-color-D30" }, "\u041F\u0440\u043E\u0444\u0438\u043B\u044C")) : null,
                React__default.createElement(Text, { strong: true, nowrap: true, className: cx('userName'), title: userName || 'Ваш профиль' }, userName || 'Ваш профиль')),
            React__default.createElement("div", { className: cx('dropdownWrapper') },
                React__default.createElement("div", { className: cx('dropdown') },
                    React__default.createElement("div", { className: cx('dropdownArrow') }),
                    React__default.createElement(UserMenu$1, { onRegionSelectClick: handleRegionSelectClick })))),
        React__default.createElement(RegionDialog, { serviceURL: serviceURL, formatPath: formatPath, regions: regions, visible: isVisibleRegionDialog, onClose: handleRegionDialogCloseClick, onChange: handleRegionDialogChange })));
};
DesktopUserBlock$1.displayName = 'DesktopUserBlock';

var LoginButton = function (props) {
    var _a = useCustomProps(), oidcHandlerPrefix = _a.oidcHandlerPrefix, fixedBackground = _a.fixedBackground;
    var authLoginLinkProps = useAuthLoginLink({ prefix: oidcHandlerPrefix, isNewWindow: true });
    return (React__default.createElement(Link, __assign({}, props, authLoginLinkProps),
        React__default.createElement(Button, { size: 36, variant: fixedBackground ? 'secondary' : 'outlined', color: "gray" }, "\u0412\u043E\u0439\u0442\u0438")));
};
LoginButton.displayName = 'LoginButton';

var modules_997b7aad = {"logo":"_15lfuny"};
/*SRAVNI__src/components/Logo/styles/Logo.module.less__SRAVNI*/;

var Logo$1 = function (props) {
    var className = props.className, rest = __rest(props, ["className"]);
    var domains = useCustomProps().domains;
    var cx = useStyles(modules_997b7aad);
    return (React__default.createElement("a", __assign({ "data-element": "logo-link", href: (domains === null || domains === void 0 ? void 0 : domains.base) ? decorateRelativeUrlByDomain('/', domains.base) : '/', className: cx('logo', className) }, rest),
        React__default.createElement(Logo$2, null)));
};
Logo$1.displayName = 'Logo';

var modules_8bb178ca = {"container":"_juw2w9","iconWrapper":"_cr26k6","icon":"_436qy3","bounceIn":"_1008q7g","bounce-in":"_15kowh2","bounceOut":"_vrytp1","bounce-out":"_1ej9p44"};
/*SRAVNI__src/components/ThemeSwitcher/styles/ThemeSwitcher.module.less__SRAVNI*/;

/**
 * @description ThemeSwitcher component.
 *
 * @component
 * @example
 * ```jsx
 * <ThemeSwitcher / >
 * ```
 */
var ThemeSwitcher$1 = React__default.forwardRef(function (props, ref) {
    var className = props.className, onClick = props.onClick, rest = __rest(props, ["className", "onClick"]);
    var cx = useStyles(modules_8bb178ca);
    var _a = __read(useTheme(), 2), theme = _a[0], setTheme = _a[1];
    var handleThemeChange = useCallback(function () {
        var themeToSet = theme === ThemeName.lager ? ThemeName.guinness : ThemeName.lager;
        onClick === null || onClick === void 0 ? void 0 : onClick(themeToSet);
        setTheme(themeToSet);
    }, [onClick, setTheme, theme]);
    return (React__default.createElement("div", __assign({ ref: ref, "data-qa": "ThemeSwitcher", className: cx(className, 'container') }, rest),
        React__default.createElement(Icon, { size: 16, className: cx('h-cursor-pointer', 'iconWrapper'), onClick: handleThemeChange, background: "gray" },
            React__default.createElement(Theme, { className: cx('icon', { bounceIn: theme === ThemeName.guinness, bounceOut: theme === ThemeName.lager }) }),
            React__default.createElement(Sun, { className: cx('icon', { bounceOut: theme === ThemeName.guinness, bounceIn: theme === ThemeName.lager }) }))));
});
ThemeSwitcher$1.displayName = 'ThemeSwitcher';

var modules_0a569f60 = {"backdrop":"_1bk9h6l","backdropActive":"_1siknaa","container":"_wwm93p","containerActive":"_1ig20rw","layout":"_wbwmh0","rightBlock":"_6dv7dg","promoWrapper":"_1t4k3gr","menu":"_18jtc7b"};
/*SRAVNI__src/components/DesktopHeader/styles/DesktopHeaderContent.module.less__SRAVNI*/;

var DesktopHeaderContent$1 = React__default.forwardRef(function (props, ref) {
    var className = props.className, restProps = __rest(props, ["className"]);
    var cx = useStyles(modules_0a569f60);
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var _a = useCustomProps(), fixedBackground = _a.fixedBackground, mode = _a.mode, enableTheme = _a.enableTheme, user = _a.user, promo = _a.promo;
    var _b = convertModeToModeFlags(mode), isShowMenu = _b.isShowMenu, isShowLogo = _b.isShowLogo, isShowUser = _b.isShowUser;
    var _c = __read(useMenuItemVisible(), 1), activeItem = _c[0];
    var activeItemDebounced = useDebounceValue(activeItem, MENU_DELAY);
    var handleThemeSwitcherClick = useCallback(function (currentTheme) {
        var _a;
        (_a = window.sravniDataLayer) === null || _a === void 0 ? void 0 : _a.push({ theme: currentTheme });
        pushAnalyticEvent("themeSwitcherClick" /* EVENTS.THEME_SWITCHER_CLICK */, { eventLabel: "\u0422\u0435\u043C\u0430 - ".concat(currentTheme) });
    }, [pushAnalyticEvent]);
    var handleAuthLoginClick = useCallback(function () {
        var callback = function () { return window.location.reload(); };
        subscribeOnLoginSuccess(callback);
        pushAnalyticEvent("login" /* EVENTS.LOGIN */);
    }, [pushAnalyticEvent]);
    var handlePromoBannerClick = useCallback(function () {
        pushAnalyticEvent("promoBannerClick" /* EVENTS.PROMO_BANNER_CLICK */);
    }, [pushAnalyticEvent]);
    var handleBrowserThemeDetectorChange = useCallback(function (currentTheme) {
        var _a;
        (_a = window.sravniDataLayer) === null || _a === void 0 ? void 0 : _a.push({ theme: currentTheme });
        pushAnalyticEvent("themeDetectorChange" /* EVENTS.THEME_DETECTOR_CHANGE */, { eventLabel: "\u0422\u0435\u043C\u0430 - ".concat(currentTheme) });
    }, [pushAnalyticEvent]);
    useBrowserThemeDetector({ enabled: enableTheme, onChange: handleBrowserThemeDetectorChange });
    var isUserExist = !!user;
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { className: cx('backdrop', { backdropActive: Boolean(activeItemDebounced) }) }),
        React__default.createElement("header", __assign({ ref: ref, "data-qa": "Header", className: cx(className, 'container', { containerActive: fixedBackground || Boolean(activeItemDebounced) }) }, restProps),
            React__default.createElement(Layout, { className: cx('layout') },
                React__default.createElement(Space, { align: "center", className: cx('leftBlock') },
                    isShowLogo ? React__default.createElement(Logo$1, null) : null,
                    isShowMenu ? React__default.createElement(DesktopMenu$1, { className: cx('menu') }) : null,
                    promo ? (React__default.createElement("div", { className: cx('promoWrapper'), onClickCapture: handlePromoBannerClick }, promo)) : null),
                React__default.createElement(Space, { size: 12, align: "center", className: cx('rightBlock') },
                    enableTheme ? React__default.createElement(ThemeSwitcher$1, { onClick: handleThemeSwitcherClick }) : null,
                    isShowUser && isUserExist ? React__default.createElement(DesktopUserBlock$1, null) : null,
                    isShowUser && !isUserExist ? React__default.createElement(LoginButton, { onClickCapture: handleAuthLoginClick }) : null))),
        React__default.createElement(Divider, null)));
});
DesktopHeaderContent$1.displayName = 'DesktopHeaderContent';

/**
 * @description Desktop header component.
 *
 * @component
 * @example
 * ```jsx
 * <DesktopHeader user={user} settings={settings} />
 * ```
 */
var DesktopHeader$1 = React__default.forwardRef(function (props, ref) {
    var _a = __read(useMemo(function () {
        var _a = __read(splitProps(props), 2), c = _a[0], rest = _a[1];
        return [decoratePropsByDefaultValues(c), rest];
    }, [props]), 2), customProps = _a[0], headerContentProps = _a[1];
    return (React__default.createElement(CustomPropsProvider, { value: customProps },
        React__default.createElement(MenuItemVisibleProvider, { value: undefined },
            React__default.createElement(DesktopHeaderContent$1, __assign({ ref: ref }, headerContentProps)))));
});
DesktopHeader$1.displayName = 'DesktopHeader';

var MenuVisibilityContext = React__default.createContext({
    isVisible: false,
    setIsVisible: function () { }
});
function MenuVisibilityProvider(props) {
    var _a = props.value, value = _a === void 0 ? false : _a, children = props.children;
    var _b = __read(React__default.useState(value), 2), isVisible = _b[0], setIsVisible = _b[1];
    var providerValue = useMemo(function () { return ({ isVisible: isVisible, setIsVisible: setIsVisible }); }, [isVisible, setIsVisible]);
    return React__default.createElement(MenuVisibilityContext.Provider, { value: providerValue }, children);
}

function useMenuVisibility() {
    var context = React.useContext(MenuVisibilityContext);
    if (!context || Object.keys(context).length === 0)
        throw new Error('useMenuVisibility hook must be inside MenuVisibilityProvider');
    var isVisible = context.isVisible, setIsVisible = context.setIsVisible;
    return [isVisible, setIsVisible];
}

var UserMenuVisibilityContext = React__default.createContext({
    isVisible: false,
    setIsVisible: function () { }
});
function UserMenuVisibilityProvider(props) {
    var _a = props.value, value = _a === void 0 ? false : _a, children = props.children;
    var _b = __read(React__default.useState(value), 2), isVisible = _b[0], setIsVisible = _b[1];
    var providerValue = useMemo(function () { return ({ isVisible: isVisible, setIsVisible: setIsVisible }); }, [isVisible, setIsVisible]);
    return React__default.createElement(UserMenuVisibilityContext.Provider, { value: providerValue }, children);
}

function useUserMenuVisibility() {
    var context = React.useContext(UserMenuVisibilityContext);
    if (!context || Object.keys(context).length === 0)
        throw new Error('useUserMenuVisibility hook must be inside UserMenuVisibilityProvider');
    var isVisible = context.isVisible, setIsVisible = context.setIsVisible;
    return [isVisible, setIsVisible];
}

var defaultMobileMenu = [
    {
        text: 'Кредиты',
        href: '/kredity/onlajn-zayavka-na-kredit/',
        customStyle: '',
        isLink: true,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Подбор кредита',
                        href: '/kredity/onlajn-zayavka-na-kredit/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Wallet.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Кредитный рейтинг',
                        href: '/kredity/scoring/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Scoring.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Потребительские кредиты',
                        href: '/kredity/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Orders.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Рефинансирование',
                        href: '/kredity/promo/refinansirovanie/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Exchange.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Автокредиты',
                        href: '/avtokredity/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Car.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Кредиты для бизнеса',
                        href: '/biznes-kredity/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Briefcase.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Банкротство',
                        href: '/bankrotstvo/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/ChartDown.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: '',
                        href: '',
                        customStyle: 'border-bottom: 1px solid #E6EAEF;height: 1px;margin: 16px 0 8px;',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: false,
                        children: []
                    },
                    {
                        text: 'Курсы обмена валют',
                        href: '/valjuty/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Рейтинг банков',
                        href: '/banki/rating/klientskij/?productCategory=natural',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Список банков',
                        href: '/banki/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о банках',
                        href: '/banki/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Страхование',
        href: '',
        customStyle: '',
        isLink: false,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'ОСАГО',
                        href: '/osago/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Car.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Каско',
                        href: '/kasko/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Car.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование ипотеки',
                        href: '/strahovanie-ipoteki/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/HouseSafety.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование путешественников',
                        href: '/vzr/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Plane.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование квартиры',
                        href: '/strahovanie-nedvizhimosti/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/ShieldSafety.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Страхование спортсменов',
                        href: '/strahovanie-ns/sportivnaya/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/PrizeCup.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Добровольное медицинское страхование',
                        href: '/dms/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Stethoscope.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'От критических заболеваний',
                        href: '/strahovka-kriticheskie-bolezni/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Lungs.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование от укуса клеща',
                        href: '/strahovka-ot-klescha/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Bug.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование дома и дачи',
                        href: '/strahovanie-domov/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/InsuranceHouse.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 3',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: '',
                        href: '',
                        customStyle: 'border-bottom: 1px solid #E6EAEF;height: 1px;margin: 16px 0 8px;',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: false,
                        children: []
                    },
                    {
                        text: 'Проверка КБМ',
                        href: '/osago/proverka-kbm/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Рейтинг страховых компаний',
                        href: '/strakhovye-kompanii/rating/klientskij/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Список страховых компаний',
                        href: '/strakhovye-kompanii/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о страховых компаниях',
                        href: '/strakhovye-kompanii/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Статьи о страховании',
                        href: '/novosti/rubrika/strahovki/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Займы',
        href: '',
        customStyle: '',
        isLink: false,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Займы онлайн',
                        href: '/zaimy/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/PhoneDownload.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы на карту',
                        href: '/zaimy/na-kartu/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CreditCard.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы без отказа',
                        href: '/zaimy/na-kartu-bez-otkazov/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/ThumbUp.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы с плохой КИ',
                        href: '/zaimy/s-plokhoj-kreditnoj-istoriej/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/ChartDown.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы без процентов',
                        href: '/zaimy/bez-protsentov/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Percent.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Банкротство',
                        href: '/bankrotstvo/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/ChartDown.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Лучшие займы',
                        href: '/zaimy/top/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Fire.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы под залог ПТС',
                        href: '/zaimy/pod-pts/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Car.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы через Госуслуги',
                        href: '/zaimy/cherez-gosuslugi/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Fingerprint.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Займы до зарплаты',
                        href: '/zaimy/na-kartu-do-zarplaty/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/MyCredits.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Долгосрочные займы',
                        href: '/zaimy/dolgosrochnye/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CalendarSimple.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 3',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: '',
                        href: '',
                        customStyle: 'border-bottom: 1px solid #E6EAEF;height: 1px;margin: 16px 0 8px;',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: false,
                        children: []
                    },
                    {
                        text: 'Рейтинг МФО',
                        href: '/zaimy/rating-mfo/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Список МФО',
                        href: '/zaimy/mfo/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы об МФО',
                        href: '/zaimy/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Ипотека',
        href: '',
        customStyle: '',
        isLink: false,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Ипотечные кредиты',
                        href: '/ipoteka/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/House.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Вторичное жилье',
                        href: '/ipoteka/zhile-na-vtorichnom-rynke/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/HouseVacations.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Новостройки',
                        href: '/ipoteka/zhile-na-pervichnom-rynke/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/House.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Строительство дома',
                        href: '/ipoteka/na-stroitelstvo-doma/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/HouseSafety.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Рефинансирование ипотеки',
                        href: '/ipoteka/refinansirovanie-ipoteki/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Exchange.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Ипотека в новостройках ПИК',
                        href: '/special/pik/?promo=1',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: '//f.sravni.ru/logotypes/menu/pikLogo.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 3',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: '',
                        href: '',
                        customStyle: 'border-bottom: 1px solid #E6EAEF;height: 1px;margin: 16px 0 8px;',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: false,
                        children: []
                    },
                    {
                        text: 'Рейтинг банков',
                        href: '/banki/rating/klientskij/?productCategory=natural',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Список банков',
                        href: '/banki/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о банках',
                        href: '/banki/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Карты',
        href: '/kredity/promo/karty/',
        customStyle: '',
        isLink: true,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Подбор кредитной карты',
                        href: '/kredity/promo/karty/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CreditCardGroupo.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Кредитные карты',
                        href: '/karty/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CardTimer.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Дебетовые карты',
                        href: '/debetovye-karty/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CardPlus.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: '',
                        href: '',
                        customStyle: 'border-bottom: 1px solid #E6EAEF;height: 1px;margin: 16px 0 8px;',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: false,
                        children: []
                    },
                    {
                        text: 'Рейтинг банков',
                        href: '/banki/rating/klientskij/?productCategory=natural',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Список банков',
                        href: '/banki/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о банках',
                        href: '/banki/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Вклады',
        href: '',
        customStyle: '',
        isLink: false,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Вклады',
                        href: '/vklady/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Deposit.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Накопительные счета',
                        href: '/vklady/nakopitelnye/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Percent.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Валютные вклады',
                        href: '/vklady/valjutnie/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/DollarSign.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Вклады для пенсионеров',
                        href: '/vklady/pensionnye/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Users.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Вклады с пополнением',
                        href: '/vklady/s-popolneniem/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CoinsPlus.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Куда вложить деньги',
                        href: '/promo/bonus/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Star.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Брокерское обслуживание',
                        href: '/invest/brokerskoe-obsluzhivanie/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Briefcase.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Инвестиции в МФО',
                        href: '/invest/investicii-v-mfo/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CoinStack.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 3',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: '',
                        href: '',
                        customStyle: 'border-bottom: 1px solid #E6EAEF;height: 1px;margin: 16px 0 8px;',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: false,
                        children: []
                    },
                    {
                        text: 'Рейтинг банков',
                        href: '/banki/rating/klientskij/?productCategory=natural',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Список банков',
                        href: '/banki/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о банках',
                        href: '/banki/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Для бизнеса',
        href: '',
        customStyle: '',
        isLink: false,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Сервис регистрации самозанятых',
                        href: '/biznes-oformlenie-samozanyatogo/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/IdentityCard.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Расчетно-кассовое обслуживание',
                        href: '/rko/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CardTerminal.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Кредиты',
                        href: '/biznes-kredity/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Briefcase.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Банковские гарантии',
                        href: '/biznes/bankovskie-garantii/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: '//f.sravni.ru/logotypes/menu/bankGuarantees.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Эквайринг',
                        href: '/biznes/ekvajring/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Acquiring.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Вклады',
                        href: '/biznes-vklady/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CoinRub.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Регистрация бизнеса',
                        href: '/biznes-registration/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/BlankStamp.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Бухгалтерские услуги',
                        href: '/biznes-accounting/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/BusinessAccounting.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Бизнес на маркетплейс',
                        href: '/biznes-marketplace',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Management.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Лизинг',
                        href: '/biznes-leasing/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/ProductsSearch.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Внесение изменений в ИП и ООО',
                        href: '/biznes-vnesenie-izmeneniy/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/CursorSelected.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Ликвидация ИП',
                        href: '/biznes-likvidaciya/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/FileCrossOut.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: '',
                        href: '',
                        customStyle: 'border-bottom: 1px solid #E6EAEF;height: 1px;margin: 16px 0 8px;',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: false,
                        children: []
                    },
                    {
                        text: 'Рейтинг банков для бизнеса',
                        href: '/banki/rating/klientskij/?productCategory=legal',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о банках',
                        href: '/banki/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Статьи о бизнесе',
                        href: '/novosti/teg/biznes/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Образование',
        href: '/kursy/',
        customStyle: '',
        isLink: true,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: false,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Программирование',
                        href: '/kursy/programmirovanie/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Programming.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Аналитика',
                        href: ' /kursy/professii-analitik/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Analytics.svg',
                        isTextVisible: false,
                        children: []
                    },
                    {
                        text: 'Маркетинг',
                        href: '/kursy/professii-marketolog/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Marketing.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Управление',
                        href: '/kursy/upravlencheskie/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Briefcase.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Английский язык',
                        href: '/kursy/anglijskij-yazyk/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Bubble.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Дизайн',
                        href: '/kursy/dizajn/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Design.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Веб-дизайн',
                        href: '/kursy/dizajn-web/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/WebDesign.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Графический дизайн',
                        href: ' /kursy/dizajn-graficheskij/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/GraphicDesign.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'QA (тестирование)',
                        href: '/kursy/programmirovanie-testirovshchik/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/QA.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: '1С',
                        href: '/kursy/programmirovanie-1s/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Programming1C.svg',
                        isTextVisible: false,
                        children: []
                    },
                    {
                        text: 'Python',
                        href: '/kursy/programmirovanie-python/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Globe.svg',
                        isTextVisible: false,
                        children: []
                    },
                    {
                        text: 'Подготовка к ЕГЭ и ОГЭ',
                        href: '/ege-oge/',
                        customStyle: '',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/BookOpen.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: 'border-bottom: 1px solid #E6EAEF;height: 1px;margin: 16px 0 8px;',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: '',
                        href: '',
                        customStyle: 'border-bottom: 1px solid #E6EAEF;height: 1px;margin: 16px 0 8px;',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: false,
                        children: []
                    },
                    {
                        text: 'Рейтинг курсов',
                        href: '/kursy/rating/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Отзывы о курсах',
                        href: '/kursy/otzyvy/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Статьи о курсах',
                        href: '/novosti/teg/obrazovanie/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
    {
        text: 'Журнал',
        href: '',
        customStyle: '',
        isLink: false,
        nofollow: false,
        noindex: false,
        openInNew: false,
        icon: null,
        iconSrc: null,
        isTextVisible: true,
        children: [
            {
                text: 'Группа 1',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: 'Главное',
                        href: '/novosti/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Calculations.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Спецпроекты',
                        href: '/promo/special-projects/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Rocket.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Тесты и игры',
                        href: '/novosti/teg/testy-i-igry/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Gamepad.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Вопросы и ответы',
                        href: '/q/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/QnA.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Блоги компаний',
                        href: '/novosti/rubrika/novosti-partnerov/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/Subscriptions.svg',
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Рейтинг экспертов',
                        href: '/u/rating/',
                        customStyle: '',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: 'https://s91588.cdn.ngenix.net/shared/icons/PrizeCup.svg',
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
            {
                text: 'Группа 2',
                href: '',
                customStyle: '',
                isLink: false,
                nofollow: false,
                noindex: false,
                openInNew: false,
                icon: null,
                iconSrc: null,
                isTextVisible: false,
                children: [
                    {
                        text: '',
                        href: '',
                        customStyle: 'border-bottom: 1px solid #E6EAEF;height: 1px;margin: 16px 0 8px;',
                        isLink: false,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: false,
                        children: []
                    },
                    {
                        text: 'Карты',
                        href: '/novosti/rubrika/karty/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Вклады',
                        href: '/novosti/rubrika/vklady/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Кредиты',
                        href: '/novosti/rubrika/kredity/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Инвестиции',
                        href: '/novosti/rubrika/investicii/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Недвижимость',
                        href: '/novosti/rubrika/nedvizhimost/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Страхование',
                        href: '/novosti/rubrika/strahovki/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                    {
                        text: 'Советы',
                        href: '/novosti/rubrika/sovety/',
                        customStyle: 'color: var(--color-D40);',
                        isLink: true,
                        nofollow: false,
                        noindex: false,
                        openInNew: false,
                        icon: null,
                        iconSrc: null,
                        isTextVisible: true,
                        children: []
                    },
                ]
            },
        ]
    },
];

var MobileMenu = function (props) {
    var onChange = props.onChange, restProps = __rest(props, ["onChange"]);
    var _a = useCustomProps().settings, settings = _a === void 0 ? {} : _a;
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var _b = settings.mobileMenu, menu = _b === void 0 ? defaultMobileMenu : _b;
    var mobileMenuTopLinksText = useMemo(function () { return menu.map(function (o) { return o.text; }).filter(function (o) { return o !== ''; }); }, [menu]);
    var _c = __read(useState(0), 2), previousAccordionIndexesLength = _c[0], setPreviousAccordionIndexesLength = _c[1];
    var promoLinks = useMemo(function () { return flatMenuLinks(menu).filter(function (item) { return item.href.includes(promoParam); }); }, [menu]);
    var handleAccordionChange = useCallback(function (currentIndexes) {
        onChange === null || onChange === void 0 ? void 0 : onChange(currentIndexes);
        var currentAccordionIndexesLength = currentIndexes.length;
        setPreviousAccordionIndexesLength(currentAccordionIndexesLength);
        var isExpandAction = currentAccordionIndexesLength > previousAccordionIndexesLength;
        var label = mobileMenuTopLinksText[currentIndexes[currentAccordionIndexesLength - 1]];
        if (isExpandAction)
            pushAnalyticEvent("topMenuShow" /* EVENTS.TOP_MENU_SHOW */, { eventLabel: "".concat(label, "|\u041A\u043B\u0438\u043A") });
    }, [mobileMenuTopLinksText, onChange, previousAccordionIndexesLength, pushAnalyticEvent]);
    var handleMenuItemClick = useCallback(function (event) {
        event.stopPropagation();
        var label = event.currentTarget.dataset.label || '';
        var section = event.currentTarget.dataset.section || '';
        pushAnalyticEvent("menuLinkClick" /* EVENTS.MENU_LINK_CLICK */, { eventLabel: "".concat(section, "|").concat(label) });
    }, [pushAnalyticEvent]);
    var handlePromoLinkIntersectionChange = useCallback(function (data) {
        var _a;
        var inView = data.inView, item = data.item, entry = data.entry;
        var text = item.text, href = item.href;
        var target = (entry === null || entry === void 0 ? void 0 : entry.target) ? entry.target : undefined;
        var section = ((_a = target === null || target === void 0 ? void 0 : target.dataset) === null || _a === void 0 ? void 0 : _a.section) || '';
        if (inView) {
            pushAnalyticEvent("promoLinkInView" /* EVENTS.PROMO_LINK_IN_VIEW */, { eventLabel: "".concat(section, "|").concat(text, "|").concat(href) });
        }
    }, [pushAnalyticEvent]);
    var _d = useCreateIntersectionObserverRefs({
        items: promoLinks,
        onChange: handlePromoLinkIntersectionChange
    }), promoIntersectionRefs = _d.refs, getPromoRefKey = _d.getRefKey;
    return (React__default.createElement(Accordion, __assign({ scrollIntoView: true, "data-qa": "Navigation" }, restProps, { onChange: handleAccordionChange }), menu.map(function (section) { return (React__default.createElement(Accordion.Item, { key: section.text, "data-label": section.text, title: section.text, style: getCustomStyles(section), unmountOnExit: false },
        React__default.createElement(Space, { size: 16, direction: "vertical" }, section.children.map(function (submenus) { return (React__default.createElement(Space, { size: 16, direction: "vertical", key: submenus.text }, submenus.children.map(function (menuItem) { return (React__default.createElement(MenuItem$1, { ref: promoIntersectionRefs[getPromoRefKey(menuItem)], key: menuItem.href, item: menuItem, "data-section": section.text, onClick: handleMenuItemClick })); }))); })))); })));
};
MobileMenu.displayName = 'MobileMenu';

var modules_5e29803a = {"triggerButton":"_etaboy","hidden":"_q5mv40","promoWrapper":"_u2yf9","dropdown":"_qr0cvi","dropdownVisible":"_puwvcp"};
/*SRAVNI__src/components/MobileMenuBlock/styles/MobileMenuBlock.module.less__SRAVNI*/;

var MobileMenuBlock$1 = function (props) {
    var promo = useCustomProps().promo;
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var cx = useStyles(modules_5e29803a);
    var _a = __read(useMenuVisibility(), 2), isVisibleMenu = _a[0], setIsVisibleMenu = _a[1];
    var handleUserDropdownButtonCloseClick = useCallback(function () {
        setIsVisibleMenu(false);
        pushAnalyticEvent("mobileBurgerClick" /* EVENTS.MOBILE_BURGER_CLICK */, { eventLabel: "\u0417\u0430\u043A\u0440\u044B\u0442\u0438\u0435" });
    }, [pushAnalyticEvent, setIsVisibleMenu]);
    var handleUserDropdownButtonOpenClick = useCallback(function () {
        setIsVisibleMenu(true);
        pushAnalyticEvent("mobileBurgerClick" /* EVENTS.MOBILE_BURGER_CLICK */, { eventLabel: "\u041E\u0442\u043A\u0440\u044B\u0442\u0438\u0435" });
    }, [pushAnalyticEvent, setIsVisibleMenu]);
    var handlePromoBannerClick = React__default.useCallback(function () {
        pushAnalyticEvent("promoBannerClick" /* EVENTS.PROMO_BANNER_CLICK */);
    }, [pushAnalyticEvent]);
    useEffect(function () {
        preventBodyScroll(isVisibleMenu);
        return function () { return preventBodyScroll(); };
    }, [isVisibleMenu]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Space, __assign({ align: "center", "data-qa": "MobileMenuBlock" }, props),
            React__default.createElement(Button, { size: 36, variant: "secondary", color: "gray", className: cx('triggerButton'), onClick: isVisibleMenu ? handleUserDropdownButtonCloseClick : handleUserDropdownButtonOpenClick },
                React__default.createElement(Icon, { icon: isVisibleMenu ? React__default.createElement(Cross, null) : React__default.createElement(Menu, null) }))),
        React__default.createElement("div", { className: cx('dropdown', { dropdownVisible: isVisibleMenu }) },
            React__default.createElement(MobileMenu, null),
            promo ? (React__default.createElement("div", { className: cx('promoWrapper'), onClickCapture: handlePromoBannerClick }, promo)) : null)));
};
MobileMenuBlock$1.displayName = 'MobileMenuBlock';

var modules_2054d19b = {"closeDialogButton":"_1lkayf7","dropdown":"_1m4oauh","dropdownVisible":"_g0ryup"};
/*SRAVNI__src/components/MobileUserBlock/styles/MobileUserBlock.module.less__SRAVNI*/;

var MobileUserBlock$1 = function (props) {
    var _a = useCustomProps(), user = _a.user, _b = _a.serviceURL, serviceURL = _b === void 0 ? '/' : _b, getServiceUrl = _a.getServiceUrl, formatPath = _a.formatPath, regions = _a.regions;
    var cx = useStyles(modules_2054d19b);
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var _c = __read(useState(false), 2), isVisibleRegionDialog = _c[0], setIsVisibleRegionDialog = _c[1];
    var _d = __read(useUserMenuVisibility(), 2), isVisibleUserMenu = _d[0], setIsVisibleUserMenu = _d[1];
    var handleUserMenuDropdownButtonCloseClick = useCallback(function () { return setIsVisibleUserMenu(false); }, [setIsVisibleUserMenu]);
    var handleUserMenuDropdownButtonOpenClick = useCallback(function () {
        setIsVisibleUserMenu(true);
        pushAnalyticEvent("userMenuShow" /* EVENTS.USER_MENU_SHOW */, { eventLabel: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C|\u041A\u043B\u0438\u043A" });
        pushAnalyticEvent("downloadApp" /* EVENTS.DOWNLOAD_APP */, {
            eventAction: 'Показ ссылки в Store',
            eventLabel: "\u041C\u0435\u043D\u044E \u043F\u0440\u043E\u0444\u0438\u043B\u044F|\u041E\u0431\u0449\u0430\u044F \u043A\u043D\u043E\u043F\u043A\u0430 \u0438 QR"
        });
    }, [setIsVisibleUserMenu, pushAnalyticEvent]);
    var handleRegionDialogCloseClick = useCallback(function () { return setIsVisibleRegionDialog(false); }, [setIsVisibleRegionDialog]);
    var handleRegionSelectClick = useCallback(function () { return setIsVisibleRegionDialog(true); }, [setIsVisibleRegionDialog]);
    var handleRegionDialogChange = useCallback(function (region) {
        pushAnalyticEvent("regionDialogClick" /* EVENTS.REGION_DIALOG_CLICK */, { eventLabel: region.name });
    }, [pushAnalyticEvent]);
    useEffect(function () {
        preventBodyScroll(isVisibleUserMenu);
        return function () { return preventBodyScroll(); };
    }, [isVisibleUserMenu]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Space, __assign({ align: "center", "data-qa": "MobileUserBlock" }, props),
            isVisibleUserMenu ? (React__default.createElement(Button, { className: cx('closeDialogButton'), size: 36, variant: "primary", color: "gray", onClick: handleUserMenuDropdownButtonCloseClick },
                React__default.createElement(Icon, { icon: React__default.createElement(Cross, null) }))) : null,
            (user === null || user === void 0 ? void 0 : user.sub) && !isVisibleUserMenu ? (React__default.createElement(Avatar, { size: 36, src: "//f.sravni.ru/avatars/".concat(user.sub, ".svg"), onClick: handleUserMenuDropdownButtonOpenClick })) : null),
        React__default.createElement("div", { className: cx('dropdown', { dropdownVisible: isVisibleUserMenu }) },
            React__default.createElement(UserMenu$1, { onRegionSelectClick: handleRegionSelectClick })),
        React__default.createElement(RegionDialog, { fullscreen: true, serviceURL: serviceURL, getServiceUrl: getServiceUrl, formatPath: formatPath, regions: regions, visible: isVisibleRegionDialog, onClose: handleRegionDialogCloseClick, onChange: handleRegionDialogChange })));
};
MobileUserBlock$1.displayName = 'MobileUserBlock';

var modules_48da1506 = {"container":"_1hsu05g","containerActive":"_hryab2","layout":"_964e4y"};
import "./assets/index-a777c7dd.css";

var MobileHeaderContent$1 = React__default.forwardRef(function (props, ref) {
    var className = props.className, restProps = __rest(props, ["className"]);
    var cx = useStyles(modules_48da1506);
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var handleThemeSwitcherClick = useCallback(function (currentTheme) {
        var _a;
        (_a = window.sravniDataLayer) === null || _a === void 0 ? void 0 : _a.push({ theme: currentTheme });
        pushAnalyticEvent("themeSwitcherClick" /* EVENTS.THEME_SWITCHER_CLICK */, { eventLabel: "\u0422\u0435\u043C\u0430 - ".concat(currentTheme) });
    }, [pushAnalyticEvent]);
    var handleAuthLoginClick = React__default.useCallback(function () {
        var callback = function () { return window.location.reload(); };
        subscribeOnLoginSuccess(callback);
        pushAnalyticEvent("login" /* EVENTS.LOGIN */);
    }, [pushAnalyticEvent]);
    var handleBrowserThemeDetectorChange = React__default.useCallback(function (currentTheme) {
        var _a;
        (_a = window.sravniDataLayer) === null || _a === void 0 ? void 0 : _a.push({ theme: currentTheme });
        pushAnalyticEvent("themeDetectorChange" /* EVENTS.THEME_DETECTOR_CHANGE */, { eventLabel: "\u0422\u0435\u043C\u0430 - ".concat(currentTheme) });
    }, [pushAnalyticEvent]);
    var _a = useCustomProps(), mode = _a.mode, user = _a.user, fixedBackground = _a.fixedBackground, enableTheme = _a.enableTheme;
    var _b = convertModeToModeFlags(mode), isShowMenu = _b.isShowMenu, isShowLogo = _b.isShowLogo, isShowUser = _b.isShowUser;
    var _c = __read(useUserMenuVisibility(), 2), isUserMenuVisible = _c[0], setIsUserMenuVisible = _c[1];
    var _d = __read(useMenuVisibility(), 2), isMenuVisible = _d[0], setIsMenuVisible = _d[1];
    var isUserExist = !!user;
    useEffect(function () {
        if (isUserMenuVisible)
            setIsMenuVisible(false);
    }, [isUserMenuVisible, setIsMenuVisible]);
    useEffect(function () {
        if (isMenuVisible)
            setIsUserMenuVisible(false);
    }, [isMenuVisible, setIsUserMenuVisible]);
    useBrowserThemeDetector({ enabled: enableTheme, onChange: handleBrowserThemeDetectorChange });
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("header", __assign({ ref: ref, "data-qa": "Header", className: cx(className, 'container', {
                containerActive: fixedBackground || isUserMenuVisible || isMenuVisible
            }) }, restProps),
            React__default.createElement(Layout, { className: cx('layout') },
                React__default.createElement(Space, { size: 12, inline: true },
                    isShowMenu ? React__default.createElement(MobileMenuBlock$1, null) : null,
                    isShowLogo ? React__default.createElement(Logo$1, null) : null),
                React__default.createElement(Space, { size: 12, inline: true },
                    enableTheme ? React__default.createElement(ThemeSwitcher$1, { onClick: handleThemeSwitcherClick }) : null,
                    isShowUser && isUserExist ? React__default.createElement(MobileUserBlock$1, null) : null,
                    isShowUser && !isUserExist ? React__default.createElement(LoginButton, { onClickCapture: handleAuthLoginClick }) : null))),
        React__default.createElement(Divider, null)));
});
MobileHeaderContent$1.displayName = 'MobileHeaderContent';

/**
 * @description Mobile header component.
 *
 * @component
 * @example
 * ```jsx
 * <MobileHeader user={user} settings={settings} />
 * ```
 */
var MobileHeader$1 = React__default.forwardRef(function (props, ref) {
    var _a = __read(useMemo(function () {
        var _a = __read(splitProps(props), 2), c = _a[0], rest = _a[1];
        return [decoratePropsByDefaultValues(c), rest];
    }, [props]), 2), customProps = _a[0], headerContentProps = _a[1];
    return (React__default.createElement(CustomPropsProvider, { value: customProps },
        React__default.createElement(UserMenuVisibilityProvider, { value: false },
            React__default.createElement(MenuVisibilityProvider, { value: false },
                React__default.createElement(MobileHeaderContent$1, __assign({ ref: ref }, headerContentProps))))));
});
MobileHeader$1.displayName = 'MobileHeader';

var Header = React__default.forwardRef(function (props, ref) {
    var isMobile = useIsMobile();
    return isMobile ? React__default.createElement(MobileHeader$1, __assign({}, props, { ref: ref })) : React__default.createElement(DesktopHeader$1, __assign({}, props, { ref: ref }));
});
Header.displayName = 'Header';

export { DesktopHeader$1 as DesktopHeader, Header, MobileHeader$1 as MobileHeader };
